// src/components/groups/OwnedGroupCard.jsx
// Horizontal card component for groups the user owns
// Redesigned January 2026: Image left (4:3), introduction right, headline below, action buttons
// Compact margins for tighter layout

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import StandardButton from '../core/StandardButton';
import { useGroupClosing } from '../../hooks/useGroupClosing';

const OwnedGroupCard = ({ group, onVisit, onEdit, onAdmin, currentUser = null }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('groups');

  // Truncate description for introduction text
  const introduction = group.description
    ? group.description.length > 100
      ? group.description.substring(0, 100) + '...'
      : group.description
    : t('no_description');

  // Closing state from the row the owner's list already holds; the hook computes who may undo and
  // keeps the card in step once Undo succeeds (Roadmap #103, brief ruling 3).
  const closingState = useGroupClosing(group.id, currentUser, {
    closing: !!group.deleted_at,
    closesOn: group.deletion_scheduled_at,
    ownerId: group.owner_id
  });
  const isDeleting = closingState.closing;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: isDeleting ? '#f9fafb' : 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        width: '300px',
        flexShrink: 0,
        opacity: isDeleting ? 0.6 : 1,
        border: isDeleting ? '1px solid #FECACA' : 'none',
        position: 'relative',
        zIndex: 1
      }}
    >
      {/* Card Body - Image left, Introduction right */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        padding: '6px',
        gap: '6px'
      }}>
        {/* Image - 4:3 ratio */}
        <div style={{
          width: '110px',
          height: '82px',
          flexShrink: 0,
          background: '#f3f4f6',
          borderRadius: '6px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {group.logo_url ? (
            <img
              src={group.logo_url}
              alt={group.name}
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>{t('no_image')}</span>
          )}
        </div>

        {/* Introduction text */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
          padding: '2px 0'
        }}>
          <p style={{
            fontSize: '12px',
            color: '#666',
            lineHeight: '1.35',
            margin: 0,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {introduction}
          </p>
        </div>
      </div>

      {/* Deletion banner */}
      {isDeleting && (
        <div style={{
          padding: '4px 8px',
          backgroundColor: '#FEE2E2',
          borderTop: '1px solid #FECACA',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{ fontSize: '12px' }}>&#9888;</span>
          <span style={{
            fontSize: '11px',
            color: '#991B1B',
            fontWeight: '600',
            lineHeight: '1.3'
          }}>
            {closingState.undoFailed
              ? t('closing_undo_failed')
              : (closingState.closesOn
                ? t('closing_date', { date: new Date(closingState.closesOn).toLocaleDateString(formattingLocale) })
                : t('closing_notice_undated'))}
          </span>
          {closingState.canUndo && (
            <StandardButton
              variant="primary"
              label={t('closing_undo_button')}
              disabled={closingState.undoing}
              onClick={(e) => { e?.stopPropagation?.(); closingState.undo(); }}
              style={{ marginLeft: 'auto' }}
            />
          )}
        </div>
      )}

      {/* Headline */}
      <div style={{
        padding: '4px 6px',
        borderTop: '1px solid #eee'
      }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          margin: 0,
          color: isDeleting ? '#991B1B' : '#1e293b',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {group.name}
        </h3>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '6px',
        padding: '4px 6px 6px',
        borderTop: '1px solid #eee'
      }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onVisit(group);
          }}
          style={{
            flex: 1,
            padding: '6px 8px',
            background: theme.primary,
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          {t('visit_group_button')}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(group);
          }}
          style={{
            flex: 1,
            padding: '6px 8px',
            background: theme.accent,
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          {t('edit_group')}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAdmin(group);
          }}
          style={{
            flex: 1,
            padding: '6px 8px',
            background: theme.secondary || '#64748b',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          {t('group_admin_button')}
        </button>
      </div>
    </div>
  );
};

export default OwnedGroupCard;
